import { Stack, Text, Group, TextInput, Button } from '@mantine/core';
import { Search, X } from 'lucide-react';
import { useMemo } from 'react';
import { OneOnOneHistoryTable } from './OneOnOneHistoryTable';
import { useDataLibraryOneOnOneHistory } from './useDataLibraryOneOnOneHistory';
import { ONE_ON_ONE_HISTORY_EVENT_LABELS } from './oneOnOneHistoryColumns';
import { FilterPanel } from '../../common/FilterPanel/FilterPanel';
import type { ActiveFilterRow, FilterFieldConfig } from '../../common/FilterPanel/FilterPanel';
import { GroupPanel } from '../../common/GroupPanel/GroupPanel';
import { deriveGroupFields } from '../../../utils/groupDataByField';
import { ColumnSettingsPopover } from '../../common/ColumnSettingsPopover/ColumnSettingsPopover';
import { clearThenFocusFilter } from '../../../utils/focusFilterTrigger';
import type { OneOnOneHistoryEvent } from '../../../types';

/** MED-1346: every date and status change on the 1:1s, newest first. */
export function DataLibraryOneOnOneHistoryTab({
  selectedCohortIds,
  initialFilters,
}: {
  selectedCohortIds: string[];
  initialFilters?: ActiveFilterRow[];
}) {
  const {
    rows,
    groupedRows,
    loading,
    totalCount,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize,
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    activeGroup,
    setActiveGroup,
    columns,
    setColumns,
    visibleColumns,
    groupLimitTotal,
  } = useDataLibraryOneOnOneHistory({ selectedCohortIds, initialFilters });

  const groupFields = useMemo(() => deriveGroupFields(columns), [columns]);

  const filterFields: FilterFieldConfig[] = useMemo(
    () => [
      ...(filterOptions.cohorts.length > 1
        ? [{ key: 'cohort', label: 'Cohort', type: 'select' as const, options: filterOptions.cohorts }]
        : []),
      { key: 'coach', label: 'Coach', type: 'select' as const, options: filterOptions.coaches },
      { key: 'participant', label: 'Participant', type: 'select' as const, options: filterOptions.participants },
      {
        key: 'event',
        label: 'Event',
        type: 'select' as const,
        options: filterOptions.events.map(e => ({
          value: e.value,
          label: ONE_ON_ONE_HISTORY_EVENT_LABELS[e.value as OneOnOneHistoryEvent] ?? e.label,
        })),
      },
    ],
    [filterOptions],
  );

  return (
    <Stack gap="sm">
      <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
        <Group gap="xs" wrap="wrap">
          <TextInput
            placeholder="Search 1:1 history..."
            size="sm"
            leftSection={<Search size={14} />}
            rightSection={
              searchValue ? <X size={14} style={{ cursor: 'pointer' }} onClick={() => setSearchValue('')} /> : null
            }
            value={searchValue}
            onChange={e => setSearchValue(e.currentTarget.value)}
            w={220}
          />
          <FilterPanel
            quickFilters={[]}
            activeQuickFilters={[]}
            onQuickFilterToggle={() => {}}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterAdd={handleFilterAdd}
            onFilterChange={handleFilterChange}
            onFilterRemove={handleFilterRemove}
            onClearAll={clearAllFilters}
            size="sm"
          />
          <GroupPanel
            groupFields={groupFields}
            activeGroup={activeGroup}
            limitTotal={groupLimitTotal}
            onGroupChange={setActiveGroup}
            size="sm"
          />
          {hasActiveFilters && (
            <Button variant="subtle" size="sm" color="gray" leftSection={<X size={14} />} onClick={clearThenFocusFilter(clearAllFilters)}>
              Clear
            </Button>
          )}
        </Group>

        <Group gap="xs">
          <Text size="sm" c="dimmed">
            {totalCount} change{totalCount !== 1 ? 's' : ''}
          </Text>
          <ColumnSettingsPopover columns={columns} onColumnsChange={setColumns} zIndex={1000} />
        </Group>
      </Group>

      <OneOnOneHistoryTable
        data={rows}
        groupedData={groupedRows}
        columns={visibleColumns}
        sortConfig={sortConfig}
        onSort={handleSort}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearAllFilters}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </Stack>
  );
}
